import type {ReactElement} from 'react';
import {useCallback, useMemo, useState} from 'react';
import type {ChatHistoryListDisplayItem, ChatHistoryListProps} from './chat-history-list.types';
import {ChatHistoryDisplayType} from './chat-history-list.types';
import {HISTORY_FETCH_LIMIT} from './chat-history-list.types';
import styles from './chat-history-list.module.scss';
import {Text, TextSize} from '@Components/text';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {InfiniteScrollVerticalList} from '@Components/infinite-scroll-vertical-list/infinite-scroll-vertical-list';
import {ChatHistoryListItem} from '@/ai-chat/components/chat-history-list/components/chat-history-list-item/chat-history-list-item';
import {getStartOfDay} from '@Utils/date.util';
import type {HistoryItem} from '@AiChat/ai-chat.types';

const SEARCH_SKELETON_COUNT = 3;

export function ChatHistoryList(props: ChatHistoryListProps): ReactElement {
  const {actions, state} = useAiChat();
  const [loading, setLoading] = useState(false);

  const getGroupTitle = (timestamp: number): string => {
    const today = getStartOfDay(new Date());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const itemDate = getStartOfDay(new Date(timestamp * 1000));

    if (itemDate.getTime() === today.getTime()) {
      return window.i18next.t('pmwjs_today');
    }

    if (itemDate.getTime() === yesterday.getTime()) {
      return window.i18next.t('pmwjs_yesterday');
    }

    if (itemDate >= sevenDaysAgo) {
      return window.i18next.t('pmwjs_7_days');
    }

    return window.i18next.t('pmwjs_older');
  };

  const groupHistoryItems = (items: HistoryItem[]): ChatHistoryListDisplayItem[] => {
    if (!items || items.length === 0) {
      return [];
    }

    const grouped: ChatHistoryListDisplayItem[] = [];
    let lastGroupTitle: string | null = null;

    items.forEach((item) => {
      const currentGroupTitle = getGroupTitle(item.updatedAt);

      if (currentGroupTitle !== lastGroupTitle) {
        grouped.push({type: ChatHistoryDisplayType.HEADER, title: currentGroupTitle});
        lastGroupTitle = currentGroupTitle;
      }

      grouped.push({type: ChatHistoryDisplayType.ITEM, data: item});
    });

    return grouped;
  };

  const groupedHistory = useMemo(() => groupHistoryItems(state.chatHistory), [state.chatHistory]);

  const fetchMore = useCallback(async () => {
    setLoading(true);
    try {
      await actions.fetchChatHistory(state.searchQuery, state.chatHistory.length, HISTORY_FETCH_LIMIT);
    } finally {
      setLoading(false);
    }
  }, [actions, state.chatHistory.length, state.searchQuery]);

  const renderDisplayItem = (item: ChatHistoryListDisplayItem): ReactElement => {
    if (item.type === ChatHistoryDisplayType.HEADER) {
      const isFirstHeader = groupedHistory[0] === item;
      return (
        <Text
          key={`history-group-label-item-${item.title}`}
          className={`content-sub-text ${isFirstHeader ? '' : 'spacing-m-t-3'} spacing-m-b-1 ${styles.historyGroupLabel}`}
          val={item.title}
          size={TextSize.XSMALL}
        />
      );
    }

    return <ChatHistoryListItem key={item.data.idAiChat} historyItem={item.data} />;
  };

  const getSkeletonItem = (index: number): ReactElement => {
    return (
      <div key={`skeleton-${index}`} className={styles.skeletonItem}>
        <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} className={styles.skeletonThumb} />
        <SkeletonBrick theme={SkeletonBrickTheme.LIGHT_TWO} className={styles.skeletonText} />
      </div>
    );
  };

  const getSearchSkeletons = (): ReactElement => {
    return (
      <div className={getClassnames()}>
        <Text className={`content-sub-text spacing-m-b-1 ${styles.historyGroupLabel}`} val={window.i18next.t('pmwjs_search_results')} size={TextSize.XSMALL} />
        {Array.from({length: SEARCH_SKELETON_COUNT}, (_, index) => getSkeletonItem(index))}
      </div>
    );
  };

  const getClassnames = (): string => {
    return `flex-v-row gap-2 spacing-p-l-4 spacing-p-r-4 spacing-p-b-4 _overflow-auto ${styles.historyContainer}`;
  };

  if (state.isChatHistorySearchLoading) {
    return getSearchSkeletons();
  }

  return (
    <InfiniteScrollVerticalList<ChatHistoryListDisplayItem>
      containerRef={props.containerRef}
      fetchItems={fetchMore}
      fetchLimit={HISTORY_FETCH_LIMIT}
      renderItem={renderDisplayItem}
      renderSkeleton={getSkeletonItem}
      className={getClassnames()}
      items={groupedHistory}
      hasMore={state.hasMoreHistory}
      isLoading={loading}
      containScroll
    />
  );
}
